import * as React from 'react';
import { observer } from 'src/utils/mobx-react';
import Icon from 'src/components/Icon/Icon';
import { EventResultsSettingsSelectionState } from './EventResultsSettingsSelectionState';
import { EventResultsSettingsState } from './EventResultsSettingsState';
import classNames from 'classnames';

const renderLoading = (label: string): React.ReactElement => {
    return (
        <a
            href='#'
            className='btn btn-warning disabled'
            onClick={(e) => {
                e.preventDefault();
            }}
        >
            {label}
        </a>
    );
};

const renderButton = (selectionState: EventResultsSettingsSelectionState): React.ReactElement => {
    const status = selectionState.getSelectionStatus();
    const disabledClass = selectionState.unlock === false || status === 'to-be-populated' ? ' disabled ' : '';
    const disabledUnsettleClass = selectionState.unlock ? '' : ' disabled ';
    const isSettled = selectionState.isSelectionSettled;

    if (selectionState.loading !== null) {
        return renderLoading(selectionState.loading);
    }

    if (isSettled) {
        return (
            <div className='settled-box'>
                <span className='settled-label'>
                    <Icon
                        // title='yes'
                        name='checkmark'
                    />
                </span>
                <a
                    href='#'
                    className={`btn${disabledUnsettleClass}`}
                    onClick={async (e): Promise<void> => {
                        e.preventDefault();
                        await selectionState.unsettle();
                    }}
                >
                    Unsettle
                </a>
            </div>
        );
    }

    if (status === 'to-be-settled') {
        return (
            <a
                href='#'
                className={`btn ${disabledClass}`}
                onClick={async (e): Promise<void> => {
                    e.preventDefault();
                    await selectionState.settle();
                }}
            >
                Settle
            </a>
        );
    }

    return (
        <a
            href='#'
            className={`btn${disabledClass}`}
            onClick={async (e): Promise<void> => {
                e.preventDefault();
                await selectionState.save();
            }}
        >
            Save
        </a>
    );
};

interface PropsType {
    selectionState: EventResultsSettingsSelectionState;
    settingsState: EventResultsSettingsState;
}

export const EventResultsSettingsSelection = observer('EventResultsSettingsSelection', (props: PropsType) => {
    const { settingsState, selectionState } = props;

    const selectionModel = selectionState.selectionModel;
    const isSettled = selectionState.isSelectionSettled;
    const isPlace = settingsState.isPlace;
    const isSpInputError = selectionState.isSpInputError;

    return (
        <tr>
            <td>{selectionModel?.name ?? ''}</td>
            <td className='element'>
                <select
                    value={selectionState.resultInput.value}
                    onChange={(e): void => selectionState.resultInput.setValue(e.target.value)}
                    disabled={isSettled}
                >
                    <option value='unresult' />
                    <option value='won'>Win</option>
                    <option value='halfwon'>Half Win</option>
                    <option value='lost'>Lose</option>
                    <option value='halflost'>Half Lose</option>
                    <option value='place'>Place</option>
                    <option value='void'>Void</option>
                </select>
            </td>
            {isPlace ? (
                <td className='element'>
                    {selectionState.isPlace ? (
                        <input
                            type='text'
                            value={selectionState.placeInput.value}
                            onChange={(e): void => selectionState.placeInput.setValue(e.target.value)}
                            disabled={isSettled}
                        />
                    ) : undefined}
                </td>
            ) : (
                <td style={{ display: 'none' }} />
            )}
            {settingsState.isSp ? (
                <td className='element'>
                    <div
                        className={classNames([
                            'element',
                            {
                                error: isSpInputError,
                                invalid: isSpInputError,
                            },
                        ])}
                    >
                        <input
                            type='text'
                            disabled={isSettled}
                            value={selectionState.spInput.value}
                            onChange={(e): void => selectionState.spInput.setValue(e.target.value)}
                        />
                        {isSpInputError && (
                            <div className='error-message'>
                                <div className='error-message-content'>You have to enter SP before Settle</div>
                            </div>
                        )}
                    </div>
                </td>
            ) : (
                <td style={{ display: 'none' }} />
            )}
            <td className='element'>
                <input
                    type='text'
                    value={selectionState.winReductionInput.value}
                    onChange={(e): void => selectionState.winReductionInput.setValue(e.target.value)}
                    disabled={isSettled}
                />
            </td>
            {selectionState.eachWayMarket.map((item) => {
                const key = `${item.places.toString()}-${item.reduction}`;
                const inputState = selectionState.reductionInputMap.get(item.places).get(item.reduction);
                const value = inputState.value;
                const onChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
                    e.preventDefault();
                    inputState.setValue(e.target.value);
                };

                return (
                    <td
                        key={key}
                        className='element'
                    >
                        <input
                            type='text'
                            value={value}
                            onChange={onChange}
                            disabled={isSettled}
                        />
                    </td>
                );
            })}
            <td className='element checkbox'>
                <label>
                    <input
                        type='checkbox'
                        checked={selectionState.unlock}
                        onChange={(e): void => selectionState.setUnlock(e.target.checked)}
                    />
                    <span>&nbsp;</span>
                </label>
            </td>

            <td>{renderButton(selectionState)}</td>
        </tr>
    );
});
